import { z } from 'zod';

// src/widget/annotation/annotation.schema.ts
var ANNOTATION_TEXT_MAX = 280;
var ANNOTATION_SELECTOR_MAX = 500;
var ANNOTATION_MAX_DURATION_MS = 12e4;
var AnnotationKindSchema = /* @__PURE__ */ z.enum([
  "highlight",
  "spotlight",
  "arrow",
  "bubble",
  "tooltip",
  "mark"
]);
var TEXT_REQUIRED_KINDS = /* @__PURE__ */ new Set(["bubble", "tooltip"]);
var ANNOTATION_CONTROL_MAX = 36;
var ANNOTATION_CONTROL_PATTERN = /^uic_[a-z0-9]{1,32}$/;
var AnnotationTargetSchema = /* @__PURE__ */ z.object({
  /** CSS selector for the element to anchor to. */
  selector: z.string().min(1).max(ANNOTATION_SELECTOR_MAX).optional(),
  /** Opaque ui_scan handle for a previously-resolved element. */
  handle: z.string().min(1).max(200).optional(),
  /** A run of visible text to find + anchor to (inline text-range highlight). */
  phrase: z.string().min(1).max(ANNOTATION_TEXT_MAX).optional(),
  /** Optional selector scoping the `phrase` search to one subtree. */
  within: z.string().min(1).max(ANNOTATION_SELECTOR_MAX).optional(),
  /**
   * PLAN §4b PR-3a — the DURABLE identity of a control the platform already
   * knows about, as a `uic_` signature.
   *
   * The anchor the planning surfaces speak in. A `selector` is a guess about
   * markup the model has usually never seen, and a `phrase` is matched
   * literally against visible text — so both fail on the case this exists for,
   * where the platform knows a control is there because it has OBSERVED it. An
   * id resolves against what the page IS rather than against how it happens to
   * be written, which is why it survives a rename, a restyle and a
   * translation the way the other two do not.
   *
   * No selector is ever stored server-side for this: the server holds the
   * identity, and only the browser can turn one into an element.
   */
  control: z.string().regex(ANNOTATION_CONTROL_PATTERN).max(ANNOTATION_CONTROL_MAX).optional(),
  /**
   * The control's visible label, as the platform last observed it — the LAST
   * rung of the `control` ladder (identity → signature → phrase).
   *
   * A durable identity can outlive the signature it was minted from: a control
   * genuinely renamed by the customer binds to a NEW signature, so an id
   * carried from an earlier observation resolves to nothing. Falling back to
   * the label lets the step land anyway, and it degrades in the safe direction
   * — the same literal-then-semantic ladder a `phrase` step already takes.
   *
   * Only meaningful with `control`; refused alone, because a label with no
   * identity behind it is simply a `phrase` written in the wrong field.
   */
  label: z.string().min(1).max(ANNOTATION_TEXT_MAX).optional()
}).refine(
  (t) => [t.selector, t.handle, t.phrase, t.control].filter((v) => v !== void 0).length === 1,
  { message: "provide exactly one of `selector`, `handle`, `phrase`, or `control`" }
).refine((t) => t.within === void 0 || t.phrase !== void 0, {
  message: "`within` is only valid together with `phrase`",
  path: ["within"]
}).refine((t) => t.label === void 0 || t.control !== void 0, {
  message: "`label` is only valid together with `control`",
  path: ["label"]
});
var AnnotationSchema = /* @__PURE__ */ z.object({
  kind: AnnotationKindSchema,
  target: AnnotationTargetSchema,
  /** Copy shown for bubble / tooltip (and an optional caption for others). */
  text: z.string().min(1).max(ANNOTATION_TEXT_MAX).optional(),
  /**
   * Auto-dismiss after N ms. Absent = the widget's standalone-overlay default
   * applies (a ~15s TTL + user-dismiss affordances: Escape / the dismiss
   * chip). 0 = pinned until the agent clears it or a new turn replaces it.
   * Walkthrough step highlights don't self-dismiss (the tour paces them).
   */
  durationMs: z.number().int().min(0).max(ANNOTATION_MAX_DURATION_MS).optional()
}).refine((a) => !TEXT_REQUIRED_KINDS.has(a.kind) || a.text !== void 0 && a.text.length > 0, {
  message: "bubble and tooltip annotations require `text`",
  path: ["text"]
});
var AnnotationPayloadSchema = /* @__PURE__ */ z.object({
  annotation: AnnotationSchema
});
var WALKTHROUGH_MAX_STEPS = 12;
var WALKTHROUGH_TITLE_MAX = 120;
var WALKTHROUGH_ROUTE_ID_MAX = 128;
var WALKTHROUGH_ROUTE_ID_PATTERN = /^[A-Za-z0-9_][A-Za-z0-9_.-]*$/;
var WALKTHROUGH_PERFORM_VALUE_MAX = 120;
var WalkthroughPerformSchema = /* @__PURE__ */ z.object({
  /** For a `<select>` only: which of ITS OWN options to choose. */
  value: z.string().min(1).max(WALKTHROUGH_PERFORM_VALUE_MAX).optional()
}).strict();
var WalkthroughStepSchema = /* @__PURE__ */ z.object({
  /** Where this step points (a selector / handle / phrase — same anchors as annotations). */
  target: AnnotationTargetSchema,
  /** The instruction for this step (customer/agent copy, shown verbatim). */
  text: z.string().min(1).max(ANNOTATION_TEXT_MAX),
  /** How to highlight the step's target. Defaults to `spotlight` (dim the rest). */
  kind: AnnotationKindSchema.default("spotlight"),
  /**
   * PLAN §3 PR-2 — the operator-approved route this step lives on, when it is
   * not the one the visitor is currently reading.
   *
   * The model SELECTS a destination and never AUTHORS one (the ADR-0443/0456
   * pattern): this is an id from the set of nav routes the turn already
   * advertised, never a path and never a URL. Membership is re-checked twice
   * downstream — server-side against the turn's advertised set before the
   * payload is streamed, and again in the browser against the live action
   * registry — so an id that was not approved cannot navigate anything, and a
   * step carrying one simply behaves as it does today.
   *
   * Absent on the overwhelming majority of steps: a tour that stays on one page
   * needs no destination, and SPA routing a visitor performs themselves is
   * already handled by the run's own navigation grace.
   */
  routeId: z.string().min(1).max(WALKTHROUGH_ROUTE_ID_MAX).regex(WALKTHROUGH_ROUTE_ID_PATTERN).optional(),
  /**
   * NEXT-LEVEL-PLAN unit D — ask the tour to advance this step by pressing its
   * own target rather than waiting for the visitor. See
   * {@link WalkthroughPerformSchema} for why this carries no action name.
   *
   * Absent on most steps, and absent is the whole pre-unit-D tour: the step is
   * anchored, highlighted, and waits. Nothing about that path changed.
   */
  perform: WalkthroughPerformSchema.optional()
});
var WalkthroughSchema = /* @__PURE__ */ z.object({
  /** Optional heading shown above the step counter. */
  title: z.string().min(1).max(WALKTHROUGH_TITLE_MAX).optional(),
  steps: z.array(WalkthroughStepSchema).min(1).max(WALKTHROUGH_MAX_STEPS)
});

// src/task-guides/task-guide-id.ts
var TASK_GUIDE_ID_PATTERN = /^tgd_[a-z0-9]{1,32}$/;
var TASK_GUIDE_ID_MAX = 36;

export { ANNOTATION_CONTROL_MAX, ANNOTATION_CONTROL_PATTERN, ANNOTATION_MAX_DURATION_MS, ANNOTATION_SELECTOR_MAX, ANNOTATION_TEXT_MAX, AnnotationKindSchema, AnnotationPayloadSchema, AnnotationSchema, AnnotationTargetSchema, TASK_GUIDE_ID_MAX, TASK_GUIDE_ID_PATTERN, TEXT_REQUIRED_KINDS, WALKTHROUGH_MAX_STEPS, WALKTHROUGH_PERFORM_VALUE_MAX, WALKTHROUGH_ROUTE_ID_MAX, WALKTHROUGH_ROUTE_ID_PATTERN, WALKTHROUGH_TITLE_MAX, WalkthroughPerformSchema, WalkthroughSchema, WalkthroughStepSchema };
//# sourceMappingURL=chunk-KR425E5Z.js.map
//# sourceMappingURL=chunk-KR425E5Z.js.map